/**
 * WithdrawalRateOptimizationTab - Sensitivity Analysis for Withdrawal Rate
 * 
 * This component allows users to compare Monte Carlo simulation results across
 * different portfolioEntnahmeRate (withdrawal rate) configurations to find the optimal value.
 * 
 * @author Portfolio-3Topf Team
 * @version 1.0.0
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Icon,
  Input,
  LineChart,
  Popover,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { WithdrawalRateComparisonConfig, WithdrawalRateComparisonResult } from '@/types/simulation';
import { CombinedMonteCarloEngine } from '@/services/CombinedMonteCarloEngine';
import { SteuerParameter } from '@/types';
import { SIMULATION_CONFIG } from '@/config/simulation';

interface WithdrawalRateOptimizationTabProps {
  steuerParameter: SteuerParameter | null;
  monteCarloEngine: CombinedMonteCarloEngine | undefined;
  iterations: number;
}

export const WithdrawalRateOptimizationTab: React.FC<WithdrawalRateOptimizationTabProps> = ({
  steuerParameter,
  monteCarloEngine,
  iterations,
}) => {
  // State management
  const [config, setConfig] = useState<WithdrawalRateComparisonConfig>({
    minRate: 2.0,
    maxRate: 6.0,
    stepSize: 0.25, // Fixed at 0.25%
  });
  const [results, setResults] = useState<WithdrawalRateComparisonResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentRate, setCurrentRate] = useState<number | undefined>();
  const [totalConfigs, setTotalConfigs] = useState<number | undefined>();

  // Run the optimization analysis
  const handleRunOptimization = async () => {
    if (!monteCarloEngine || !steuerParameter) {
      return;
    }

    setIsRunning(true);
    setProgress(0);
    setResults([]);

    try {
      const comparisonResults = await monteCarloEngine.runWithdrawalRateComparison(
        iterations,
        config,
        (prog, rate, total) => {
          setProgress(Math.round(prog));
          setCurrentRate(rate);
          setTotalConfigs(total);
        }
      );

      setResults(comparisonResults);
    } catch (error) {
      console.error('Optimization failed:', error);
    } finally {
      setIsRunning(false);
      setProgress(0);
      setCurrentRate(undefined);
      setTotalConfigs(undefined);
    }
  };

  // Apply client-side filtering to results
  // MEMORY OPTIMIZATION: Filtering disabled - rawResults are empty to save memory
  const filteredResults = useMemo(() => {
    return results;
  }, [results]);

  // Find optimal configuration from filtered results
  // Optimale Entnahme-Rate = höchste Rate mit Erfolgsquote >= Schwelle
  // Fallback: höchste Erfolgsquote, falls keine Rate die Schwelle erreicht
  const optimalConfig = useMemo(() => {
    if (filteredResults.length === 0) return null;

    const threshold = SIMULATION_CONFIG.OPTIMAL_WITHDRAWAL_SUCCESS_THRESHOLD * 100;

    // Find all rates meeting the threshold
    const meetsThreshold = filteredResults.filter(
      r => r.statistics.threeTopfSuccessRate >= threshold
    );

    if (meetsThreshold.length > 0) {
      // Return highest rate among those meeting threshold
      return meetsThreshold.reduce((best, current) =>
        current.withdrawalRate > best.withdrawalRate ? current : best
      );
    }

    // Fallback: return rate with highest success rate
    return filteredResults.reduce((best, current) =>
      current.statistics.threeTopfSuccessRate > best.statistics.threeTopfSuccessRate
        ? current
        : best
    );
  }, [filteredResults]);

  // Calculate failure risk metrics for each configuration
  // MEMORY OPTIMIZATION: Use statistics directly instead of rawResults
  const failureRiskData = useMemo(() => {
    if (!filteredResults || filteredResults.length === 0) {
      return [];
    }

    return filteredResults.map(result => {
      // Use pre-calculated failure metrics from the engine
      const failureRate = result.statistics.failureRate;
      const metrics = result.failureMetrics;
      
      return {
        withdrawalRate: result.withdrawalRate,
        failureRate: failureRate,
        avgYearsToFailure: metrics.avgYearsToFailure,
        medianYearsToFailure: metrics.medianYearsToFailure,
        worstCaseYear: metrics.worstCaseYear,
        bestCaseYear: metrics.bestCaseYear,
        totalFailures: metrics.totalFailures,
        totalIterations: metrics.totalIterations,
      };
    });
  }, [filteredResults]);

  // Chart data for success rate visualization (uses filtered results)
  const successRateChartData = filteredResults.length > 0
    ? [
        {
          title: '3-Topf Erfolgsquote',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.withdrawalRate.toFixed(2), // Format as "3.25"
            y: r.statistics.threeTopfSuccessRate,
          })),
          color: '#0073bb',
        },
        {
          title: '1-Topf Erfolgsquote',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.withdrawalRate.toFixed(2), // Format as "3.25"
            y: r.statistics.oneTopfSuccessRate,
          })),
          color: '#00a86b',
        },
      ]
    : [];

  // Chart data for average final balance (uses filtered results)
  const balanceChartData = filteredResults.length > 0
    ? [
        {
          title: '3-Topf Ø Endsaldo',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.withdrawalRate.toFixed(2),
            y: r.statistics.threeTopfAvgFinalBalance,
          })),
          color: '#0073bb',
        },
        {
          title: '1-Topf Ø Endsaldo',
          type: 'line' as const,
          data: filteredResults.map(r => ({
            x: r.withdrawalRate.toFixed(2),
            y: r.statistics.oneTopfAvgFinalBalance,
          })),
          color: '#00a86b',
        },
      ]
    : [];

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* Configuration Section */}
      <Container
        header={
          <Header 
            variant="h2"
            description="Konfigurieren Sie den Bereich der zu testenden Entnahmeraten"
          >
            Entnahme-Rate Optimierung Konfiguration
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <ColumnLayout columns={2}>
            <FormField
              label="Minimale Entnahme-Rate (%)"
              description="Niedrigste zu testende Entnahme-Rate"
            >
              <Input
                type="number"
                value={config.minRate.toString()}
                onChange={({ detail }) => {
                  const value = parseFloat(detail.value) || 0;
                  setConfig({ ...config, minRate: Math.max(0.1, Math.min(value, 20)) });
                }}
                step={0.25}
                disabled={isRunning}
              />
            </FormField>

            <FormField
              label="Maximale Entnahme-Rate (%)"
              description="Höchste zu testende Entnahme-Rate"
            >
              <Input
                type="number"
                value={config.maxRate.toString()}
                onChange={({ detail }) => {
                  const value = parseFloat(detail.value) || 0;
                  setConfig({ ...config, maxRate: Math.max(0.1, Math.min(value, 20)) });
                }}
                step={0.25}
                disabled={isRunning}
              />
            </FormField>
          </ColumnLayout>

          <Box>
            <Box variant="awsui-key-label">Schrittweite</Box>
            <Box variant="p">0.25% (fest)</Box>
            <Box variant="small" color="text-body-secondary">
              Die Analyse testet Entnahmeraten in 0,25%-Schritten zwischen Minimum und Maximum
            </Box>
          </Box>

          {steuerParameter && (
            <Box>
              <Box variant="awsui-key-label">Berechnete jährliche Entnahmen</Box>
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="small" color="text-body-secondary">Bei {config.minRate.toFixed(2)}%:</Box>
                  <Box variant="p">
                    {((steuerParameter.vorhandenesKapital * config.minRate) / 100).toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                      maximumFractionDigits: 0,
                    })}
                  </Box>
                </div>
                <div>
                  <Box variant="small" color="text-body-secondary">Bei {config.maxRate.toFixed(2)}%:</Box>
                  <Box variant="p">
                    {((steuerParameter.vorhandenesKapital * config.maxRate) / 100).toLocaleString('de-DE', {
                      style: 'currency',
                      currency: 'EUR',
                      maximumFractionDigits: 0,
                    })}
                  </Box>
                </div>
              </ColumnLayout>
            </Box>
          )}

          <Box>
            <Button
              variant="primary"
              onClick={handleRunOptimization}
              disabled={isRunning || !monteCarloEngine || !steuerParameter || config.minRate >= config.maxRate}
              loading={isRunning}
            >
              {isRunning ? 'Optimierung läuft...' : 'Optimierung starten'}
            </Button>
            {config.minRate >= config.maxRate && (
              <Box color="text-status-error" fontSize="body-s" margin={{ top: 'xs' }}>
                Minimale Rate muss kleiner als maximale Rate sein
              </Box>
            )}
          </Box>
        </SpaceBetween>
      </Container>

      {/* MEMORY OPTIMIZATION: Filter disabled to save memory - rawResults are empty */}
      {/* Filtering is not needed for withdrawal rate optimization */}

      <Container>
        <SpaceBetween direction="vertical" size="m">
          {/* Progress Bar */}
          {isRunning && (
            <Box>
              <Box variant="h4">Optimierung läuft...</Box>
              {currentRate !== undefined && totalConfigs !== undefined && (
                <Box variant="small" color="text-body-secondary" margin={{ bottom: 'xs' }}>
                  Entnahme-Rate: {currentRate.toFixed(2)}% | Fortschritt: {progress}%
                </Box>
              )}
              <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                <div
                  style={{
                    width: `${progress}%`,
                    height: '100%',
                    backgroundColor: '#0073bb',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </Box>
          )}

          {/* Optimal Configuration Alert */}
          {optimalConfig && !isRunning && (
            <Alert
              type="success"
              header={`Optimale Entnahme-Rate: ${optimalConfig.withdrawalRate.toFixed(2)}%`}
            >
              <SpaceBetween direction="vertical" size="xs">
                <Box>
                  Die höchste Entnahme-Rate mit einer Erfolgsquote von mindestens{' '}
                  <strong>{(SIMULATION_CONFIG.OPTIMAL_WITHDRAWAL_SUCCESS_THRESHOLD * 100).toFixed(0)}%</strong> ist{' '}
                  <strong>{optimalConfig.withdrawalRate.toFixed(2)}%</strong> mit einer tatsächlichen Erfolgsquote von{' '}
                  <strong>{optimalConfig.statistics.threeTopfSuccessRate.toFixed(1)}%</strong>.
                </Box>
                {steuerParameter && (
                  <Box>
                    Jährliche Entnahme: <strong>
                      {((steuerParameter.vorhandenesKapital * optimalConfig.withdrawalRate) / 100).toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      })}
                    </strong>
                  </Box>
                )}
              </SpaceBetween>
            </Alert>
          )}

          {/* Success Rate Comparison Chart */}
          {filteredResults.length > 0 && (
            <Container
              header={
                <Header variant="h3">
                  Erfolgsquoten-Vergleich
                </Header>
              }
            >
              <LineChart
                series={successRateChartData}
                xScaleType="categorical"
                yDomain={[0, 100]}
                xTitle="Entnahme-Rate (%)"
                yTitle="Erfolgsquote (%)"
                height={300}
                hideFilter={true}
                hideLegend={false}
                statusType={isRunning ? 'loading' : 'finished'}
                loadingText="Lade Daten..."
                empty={
                  <Box textAlign="center" color="text-body-secondary">
                    <p>Keine Daten verfügbar</p>
                  </Box>
                }
              />
            </Container>
          )}

          {/* Average Final Balance Chart */}
          {filteredResults.length > 0 && (
            <Container
              header={
                <Header variant="h3">
                  Durchschnittlicher Endsaldo-Vergleich
                </Header>
              }
            >
              <LineChart
                series={balanceChartData}
                xScaleType="categorical"
                xTitle="Entnahme-Rate (%)"
                yTitle="Ø Endsaldo (€)"
                height={300}
                hideFilter={true}
                hideLegend={false}
                statusType={isRunning ? 'loading' : 'finished'}
                loadingText="Lade Daten..."
                empty={
                  <Box textAlign="center" color="text-body-secondary">
                    <p>Keine Daten verfügbar</p>
                  </Box>
                }
              />
            </Container>
          )}

          {/* Comparison Table */}
          {filteredResults.length > 0 && steuerParameter && (
            <Container
              header={
                <Header variant="h3">
                  Detaillierter Vergleich
                </Header>
              }
            >
              <Table
                columnDefinitions={[
                  {
                    id: 'rate',
                    header: 'Entnahme-Rate',
                    cell: (item) => (
                      <Box>
                        {item.withdrawalRate === optimalConfig?.withdrawalRate && '★ '}
                        {item.withdrawalRate.toFixed(2)}%
                      </Box>
                    ),
                    sortingField: 'withdrawalRate',
                  },
                  {
                    id: 'annualWithdrawal',
                    header: 'Jährliche Entnahme',
                    cell: (item) => (
                      ((steuerParameter.vorhandenesKapital * item.withdrawalRate) / 100).toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      })
                    ),
                  },
                  {
                    id: 'threeTopfSuccess',
                    header: '3-Topf Erfolgsquote',
                    cell: (item) => `${item.statistics.threeTopfSuccessRate.toFixed(1)}%`,
                    sortingField: 'statistics.threeTopfSuccessRate',
                  },
                  {
                    id: 'oneTopfSuccess',
                    header: '1-Topf Erfolgsquote',
                    cell: (item) => `${item.statistics.oneTopfSuccessRate.toFixed(1)}%`,
                    sortingField: 'statistics.oneTopfSuccessRate',
                  },
                  {
                    id: 'threeTopfBalance',
                    header: 'Ø Endsaldo 3-Topf',
                    cell: (item) =>
                      item.statistics.threeTopfAvgFinalBalance.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      }),
                  },
                  {
                    id: 'oneTopfBalance',
                    header: 'Ø Endsaldo 1-Topf',
                    cell: (item) =>
                      item.statistics.oneTopfAvgFinalBalance.toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 0,
                      }),
                  },
                  {
                    id: 'advantage',
                    header: (
                      <SpaceBetween direction="horizontal" size="xs">
                        <span>Vorteil 3-Topf</span>
                        <Popover
                          size="large"
                          position="top"
                          triggerType="custom"
                          dismissButton={false}
                          content={
                            <Box variant="p">
                              Prozentsatz der Monte-Carlo-Iterationen, in denen die 3-Topf-Strategie einen höheren Endsaldo als die 1-Topf-Strategie erreichte.
                              <br /><br />
                              <strong>Interpretation:</strong><br />
                              • 50% = Beide Strategien gleich stark<br />
                              • &gt;50% = 3-Topf überwiegend besser<br />
                              • &lt;50% = 1-Topf überwiegend besser<br />
                              • ≥70% = 3-Topf klar dominant
                            </Box>
                          }
                        >
                          <Icon name="status-info" size="small" />
                        </Popover>
                      </SpaceBetween>
                    ),
                    cell: (item) => `${item.statistics.threeTopfAdvantageRate.toFixed(1)}%`,
                  },
                ]}
                items={filteredResults}
                sortingDisabled={true}
                empty={
                  <Box textAlign="center" color="text-body-secondary">
                    <p>Keine Daten verfügbar</p>
                  </Box>
                }
              />
            </Container>
          )}

          {/* Failure Risk Analysis Table */}
          {failureRiskData.length > 0 && (
            <Container
              header={
                <Header 
                  variant="h3"
                  description="Detaillierte Analyse der Versagensrisiken bei Kapitalerschöpfung"
                >
                  Failure-Risiko Analyse
                </Header>
              }
            >
              <Table
                columnDefinitions={[
                  {
                    id: 'rate',
                    header: 'Entnahme-Rate',
                    cell: (item) => (
                      <Box>
                        {item.withdrawalRate === optimalConfig?.withdrawalRate && '★ '}
                        {item.withdrawalRate.toFixed(2)}%
                      </Box>
                    ),
                    sortingField: 'withdrawalRate',
                  },
                  {
                    id: 'failureRate',
                    header: 'Failure Rate',
                    cell: (item) => (
                      <Box>
                        {item.failureRate.toFixed(1)}% ({item.totalFailures}/{item.totalIterations})
                      </Box>
                    ),
                  },
                  {
                    id: 'avgYears',
                    header: 'Ø Jahre bis Versagen',
                    cell: (item) =>
                      item.avgYearsToFailure > 0 ? item.avgYearsToFailure.toFixed(1) : '-',
                  },
                  {
                    id: 'medianYears',
                    header: 'Median Jahre bis Versagen',
                    cell: (item) =>
                      item.medianYearsToFailure > 0 ? item.medianYearsToFailure.toFixed(1) : '-',
                  },
                  {
                    id: 'worstCase',
                    header: 'Frühestes Versagen',
                    cell: (item) =>
                      item.worstCaseYear > 0 ? `${item.worstCaseYear} Jahre` : '-',
                  },
                  {
                    id: 'bestCase',
                    header: 'Spätestes Versagen',
                    cell: (item) =>
                      item.bestCaseYear > 0 ? `${item.bestCaseYear} Jahre` : '-',
                  },
                ]}
                items={failureRiskData}
                sortingDisabled={true}
                empty={
                  <Box textAlign="center" color="text-body-secondary">
                    <p>Keine Failure-Daten verfügbar</p>
                  </Box>
                }
              />
            </Container>
          )}
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
